import { SubCategoryRow } from './SubCategoryRow';
import type { SubCategoryVM } from '../../store/stash-scoped/categorySelectors';

interface SubCategoryListProps {
  parentName: string;
  subCategories: SubCategoryVM[];
  onSelect: (subCategoryId: string) => void;
}

/**
 * Vertical list of `<SubCategoryRow>`s under a parent category, with a left-side
 * connector rail + per-row elbow that visually nests the children under the
 * parent. The rail/elbows are purely decorative — they're marked aria-hidden so
 * screen readers see only the section header and the row buttons.
 *
 * `--border-medium` isn't a token in this codebase; the rail/elbows fall back to
 * the standard `border-border` color per the plan note.
 */
export function SubCategoryList({
  parentName,
  subCategories,
  onSelect,
}: SubCategoryListProps): React.JSX.Element {
  return (
    <section className="mt-8">
      <header className="flex items-baseline justify-between gap-4 px-1">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground">
          Sub-categories <span className="font-medium normal-case tracking-normal text-muted-foreground">in {parentName}</span>
        </h2>
        <span className="text-xs text-muted-foreground">tap a sub-category to see its cards</span>
      </header>

      <div className="relative mt-3 pl-6">
        {/* Vertical rail (decorative). */}
        <span
          aria-hidden="true"
          data-testid="connector-rail"
          className="pointer-events-none absolute bottom-2 top-0 w-[2px] bg-border"
          style={{ left: 7 }}
        />

        <ul className="flex flex-col gap-2">
          {subCategories.map(sub => (
            <li key={sub.id} className="relative">
              {/* Horizontal elbow connector into the rail (decorative). */}
              <span
                aria-hidden="true"
                data-testid="connector-elbow"
                className="pointer-events-none absolute top-1/2 h-[2px] w-[15px] bg-border"
                style={{ left: -17 }}
              />
              <SubCategoryRow sub={sub} onClick={() => onSelect(sub.id)} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
